import React from 'react';

import {Button, Icon, Menu, Text} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import type {SelectOption, SortOrder, SortValue} from '../types';

interface SortDropdownProps<T extends string> {
  value: SortValue<T>;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: SortValue<T>) => void;
  /** Trigger icon override. Defaults to the existing classwork sort glyph. */
  triggerIcon?: IconProp;
  /** Fixed trigger label (e.g. "Sort"). Defaults to the selected option's label + caret. */
  triggerLabel?: string;
}

const ORDER_OPTIONS: ReadonlyArray<SelectOption<SortOrder>> = [
  {value: 'asc', label: 'Ascending'},
  {value: 'desc', label: 'Descending'}
];

export const SortDropdown = <T extends string>({
  value,
  options,
  onChange,
  triggerIcon = ['far', 'arrow-up-wide-short'],
  triggerLabel
}: SortDropdownProps<T>) => {
  const selectedLabel = options.find((o) => o.value === value.field)?.label ?? '';
  return (
    <Menu position='bottom-end' positionFixed>
      <Menu.Trigger>
        {(triggerProps) => (
          <Button {...triggerProps} color='secondary' variant='outlined' data-testid='sort-trigger'>
            <Icon icon={triggerIcon} className='mr-x1' />
            <Text color='inherit'>{triggerLabel ?? selectedLabel}</Text>
            {!triggerLabel && <Icon icon='caret-down' className='ml-x1' />}
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Content>
        <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
          Sort By
        </Menu.Item>
        {options.map((option) => (
          <Menu.Item
            key={option.value}
            selected={value.field === option.value}
            rightIconProps={value.field === option.value ? {icon: 'check'} : undefined}
            onClick={() => onChange({...value, field: option.value})}
          >
            {option.label}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
          Order
        </Menu.Item>
        {ORDER_OPTIONS.map((option) => (
          <Menu.Item
            key={option.value}
            selected={value.order === option.value}
            rightIconProps={value.order === option.value ? {icon: 'check'} : undefined}
            onClick={() => onChange({...value, order: option.value})}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu>
  );
};
